import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MapboxProps } from '../mapbox';
import Mapbox from '../mapbox';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { Map as MapboxMap } from 'mapbox-gl';
import { ServiceArea } from '@bringg/types';
import _isFunction from 'lodash/isFunction';
import '../../../styles/mapbox.scss';
import ApproveServiceAreaModal from './approve-service-area-modal/approve-service-area-modal';
import useServiceAreas from '../../../custom-hooks/use-service-areas/use-service-areas';
import {
	clearSelectedArea,
	showAreas,
	removeArea,
	getSelectedArea,
	setSelectedArea
} from './utils/service-areas-supervisor';
import _isNil from 'lodash/isNil';
import _isEmpty from 'lodash/isEmpty';
import useMapboxDrawEvents from '../../../custom-hooks/mapbox/use-mapbox-event/use-mapbox-event';
import DeleteServiceAreaModal from './delete-service-area-modal/delete-service-area-modal';

interface Props extends MapboxProps {
	getAllServiceAreas: () => Promise<ServiceArea[]>;
	onCreateServiceArea?: (polygon: number[][], name: string) => Promise<ServiceArea>;
	onUpdateServiceArea?: (id: number, polygon: number[][], name: string) => Promise<ServiceArea>;
	onRemoveServiceArea?: (id: number) => Promise<boolean>;
	editable: boolean;
	translations: Translations;
	shouldFitServiceAreasBounds?: boolean;
	teamId: number;
}

export interface Translations {
	areaName: string;
	save: string;
	cancel: string;
	deleteAreaTitle: string;
	delete: string;
}

const MapboxWithServiceAreas: React.FC<Props> = props => {
	const {
		onLoaded: userOnLoaded,
		getAllServiceAreas,
		onCreateServiceArea,
		onUpdateServiceArea,
		onRemoveServiceArea,
		editable,
		translations,
		shouldFitServiceAreasBounds,
		teamId,
		...otherProps
	} = props;
	const mapContainer = useRef<MapboxMap>();
	const [draw, setDraw] = useState<MapboxDraw>();
	const initialServiceAreas = useServiceAreas(
		mapContainer.current,
		draw,
		getAllServiceAreas,
		editable,
		shouldFitServiceAreasBounds,
		teamId
	);
	const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>(initialServiceAreas);
	const [showAreaModal, setShowAreaModal] = useState(false);
	const [showDeleteAreaModal, setShowDeleteAreaModal] = useState(false);
	const [newArea, setNewArea] = useState(null);
	const [startCreationFlow, setStartCreationFlow] = useState(false);
	const [startEditFlow, setStartEditFlow] = useState(false);

	const onLoaded = (map: MapboxMap) => {
		mapContainer.current = map;
		if (_isFunction(userOnLoaded)) {
			userOnLoaded(map);
		}
		const mapboxDraw = new MapboxDraw({
			displayControlsDefault: false,
			controls: {
				polygon: editable,
				trash: editable
			}
		});
		setDraw(mapboxDraw);
		if (editable) {
			map.addControl(mapboxDraw);
		}
	};

	const onMapClicked = useCallback(
		event => {
			if (event.defaultPrevented) return;
			if (editable) {
				draw.deleteAll();
			}
			if (newArea && (startCreationFlow || startEditFlow)) {
				setShowAreaModal(true);
			}
		},
		[draw, newArea, startCreationFlow, startEditFlow]
	);
	const onPolygonCreated = area => {
		setNewArea(area);
		setSelectedArea(area.id);
		setStartCreationFlow(true);
	};
	const onPolygonUpdated = area => {
		setNewArea(area);
		setStartEditFlow(true);
	};

	const onPolygonDeleted = useCallback(
		area => {
			setShowDeleteAreaModal(true);
		},
		[draw]
	);

	useMapboxDrawEvents(mapContainer.current, draw, 'click', onMapClicked);
	useMapboxDrawEvents(mapContainer.current, draw, 'draw.create', onPolygonCreated);
	useMapboxDrawEvents(mapContainer.current, draw, 'draw.update', onPolygonUpdated);
	useMapboxDrawEvents(mapContainer.current, draw, 'draw.delete', onPolygonDeleted);

	const saveArea = async areaName => {
		if (_isEmpty(areaName)) {
			clearStates();
			return;
		}
		const feature = newArea.features[0];
		const polygon = feature.geometry.coordinates[0];
		let serviceArea: ServiceArea = null;
		if (startCreationFlow) {
			serviceArea = await onCreateServiceArea(polygon, areaName);
		} else if (startEditFlow) {
			serviceArea = await updateServiceArea(polygon, areaName);
		}
		clearStates();
		if (!_isNil(serviceArea)) {
			setServiceAreas([...serviceAreas, serviceArea]);
		}
	};

	const updateServiceArea = async (polygon, areaName) => {
		const { serviceArea: selectedServiceArea } = getSelectedArea();
		await removeArea(mapContainer.current, selectedServiceArea);
		return await onUpdateServiceArea(selectedServiceArea.id, polygon, areaName);
	};

	const deleteArea = async () => {
		const { serviceArea } = getSelectedArea();
		if (onRemoveServiceArea(serviceArea.id)) {
			removeArea(mapContainer.current, serviceArea);
		}
		setServiceAreas(
			serviceAreas.filter(item => {
				return serviceArea.id === item.id;
			})
		);
		clearStates();
	};

	const clearStates = () => {
		setShowAreaModal(false);
		clearSelectedArea();
		setNewArea(null);
		setStartEditFlow(false);
		setStartCreationFlow(false);
		setShowDeleteAreaModal(false);
		draw.trash();
	};

	const getAreaNameOrDefault = useCallback(() => {
		return newArea?.features[0]?.properties?.description ?? '';
	}, [newArea]);

	useEffect(() => {
		showAreas(mapContainer.current, draw, serviceAreas, editable, shouldFitServiceAreasBounds, teamId);
	}, [mapContainer.current, serviceAreas, shouldFitServiceAreasBounds]);

	return (
		<div className="mapbox-with-service-areas">
			<Mapbox {...otherProps} onLoaded={onLoaded} />
			<ApproveServiceAreaModal
				getDefaultName={getAreaNameOrDefault}
				visible={showAreaModal}
				onOk={saveArea}
				onCancel={clearStates}
				translations={translations}
			/>
			<DeleteServiceAreaModal
				visible={showDeleteAreaModal}
				onDelete={deleteArea}
				onCancel={clearStates}
				translations={translations}
			/>
		</div>
	);
};

export default MapboxWithServiceAreas;
